import axios from 'axios';
import { getServerUrl } from '../config/runtimeConfig';

const API_BASE_URL = getServerUrl();

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/album`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2 minutes for AI generation
});

// Request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Album Service
 * Handles album creation and AI-powered photo selection
 */
const albumService = {
  /**
   * Get all album data for a session
   * @param {string} sessionId - Session unique ID
   * @returns {Promise<Object>} Album data including embeddings, faces, clusters
   */
  async getAlbumData(sessionId) {
    const response = await apiClient.get(`/data/${sessionId}`);
    return response.data;
  },

  /**
   * Get persisted album generation artifacts from DB
   * @param {string} sessionId - Session unique ID
   * @returns {Promise<Object>} Stored prompt/response/recommendations
   */
  async getAlbumGenerationArtifacts(sessionId) {
    const response = await apiClient.get(`/generation/${sessionId}`);
    return response.data;
  },

  /**
   * Get list of supported AI models and their availability
   * @returns {Promise<Object>} { models: [{id, label, provider, available}], defaultModel }
   */
  async getSupportedAiModels() {
    const response = await apiClient.get('/ai-models');
    return response.data;
  },

  /**
   * Generate album recommendations using AI
   * @param {string} sessionId - Session unique ID
   * @param {Object} config - Album configuration
   * @returns {Promise<Object>} AI recommendations for album layout
   */
  async generateAlbumRecommendations(sessionId, config) {
    const response = await apiClient.post(`/generate/${sessionId}`, config);
    return response.data;
  },

  // ── Story / timeline ──────────────────────────────────────────────────────

  /**
   * Retrieve a previously-generated story timeline for a session.
   * Returns null storyTimeline if none has been generated yet.
   */
  async getStoryTimeline(sessionId) {
    const response = await apiClient.get(`/story/${sessionId}`);
    return response.data;
  },

  /**
   * Call the AI to generate (or regenerate) a story timeline.
   * Returns immediately with { status: 'in-progress' }. Poll getStoryProgress() for result.
   */
  async generateStoryTimeline(sessionId, options = {}) {
    const response = await apiClient.post(`/story/${sessionId}`, options);
    return response.data;
  },

  /**
   * Poll the in-progress story generation status.
   * { status: 'idle'|'in-progress'|'done'|'failed', story?, error? }
   */
  async getStoryProgress(sessionId) {
    const response = await apiClient.get(`/story/${sessionId}/progress`);
    return response.data;
  },

  /**
   * Persist a user-edited story timeline without re-running AI.
   */
  async saveStoryTimeline(sessionId, story, storySettings = null) {
    const response = await apiClient.post(`/story/${sessionId}/save`, { story, storySettings });
    return response.data;
  },

  /**
   * Delete the existing story timeline for a session (clears it from the DB).
   */
  async clearStoryTimeline(sessionId) {
    const response = await apiClient.delete(`/story/${sessionId}`);
    return response.data;
  },

  // ── Album plan (Step 6 – planning phase) ────────────────────────────────

  /**
   * Generate an album plan (AI decides budget, order, narrative roles for each scene).
   * Synchronous call — waits for the AI to return before resolving.
   * @param {string} sessionId
   * @param {{ aiModel?: string }} [options]
   */
  async generateAlbumPlan(sessionId, options = {}) {
    const response = await apiClient.post(`/story/${sessionId}/plan`, options, { timeout: 600000 });
    return response.data;
  },

  /** Load the persisted album plan. */
  async getAlbumPlan(sessionId) {
    const response = await apiClient.get(`/story/${sessionId}/plan`);
    return response.data;
  },

  /**
   * Save manual edits to the plan (budget overrides, scene reordering).
   * @param {string} sessionId
   * @param {{ scene_plan?, overrides? }} updates
   */
  async updateAlbumPlan(sessionId, updates) {
    const response = await apiClient.put(`/story/${sessionId}/plan`, updates);
    return response.data;
  },

  // ── Album layout (Step 6) ────────────────────────────────────────────────

  /**
   * Generate A4 page layouts for story scenes using AI.
   * @param {string} sessionId
   * @param {Object} options  { sceneIds?, pageBudgetOverrides?, maxImagesPerPage? }
   */
  async generateAlbumLayout(sessionId, options = {}) {
    const response = await apiClient.post(`/story/${sessionId}/layout`, options, { timeout: 600000 }); // 10 minutes
    return response.data;
  },

  /** Load the persisted album layout. */
  async getAlbumLayout(sessionId) {
    const response = await apiClient.get(`/story/${sessionId}/layout`);
    return response.data;
  },

  /** Poll in-progress layout generation status. */
  async getLayoutProgress(sessionId) {
    const response = await apiClient.get(`/story/${sessionId}/layout/progress`);
    return response.data;
  },

  /** Save edited album layouts. */
  async saveAlbumLayout(sessionId, layouts) {
    const response = await apiClient.post(`/story/${sessionId}/layout/save`, { layouts });
    return response.data;
  },

  /** Delete layouts for specified scene IDs (or all if sceneIds omitted). */
  async deleteAlbumLayouts(sessionId, sceneIds) {
    const response = await apiClient.delete(`/story/${sessionId}/layout`, { data: { sceneIds } });
    return response.data;
  },

  /** Request cancellation of an in-progress layout generation. */
  async cancelAlbumLayout(sessionId) {
    const response = await apiClient.post(`/story/${sessionId}/layout/cancel`);
    return response.data;
  },

  // ── Album template collections (hierarchy: AlbumTemplate → PageTemplates) ──

  /** List all active album template collections (each has embedded pageTemplates). */
  async getAlbumTemplates() {
    const response = await apiClient.get('/templates');
    return response.data;
  },

  /** Create a new album template collection (admin only). */
  async createAlbumTemplate(data) {
    const response = await apiClient.post('/templates', data);
    return response.data;
  },

  /** Soft-delete an album template collection (admin only). */
  async deleteAlbumTemplate(id) {
    const response = await apiClient.delete(`/templates/${id}`);
    return response.data;
  },

  /** Add a page template to an album template collection (admin only). */
  async addPageTemplate(albumTemplateId, pageTemplate) {
    const response = await apiClient.post(`/templates/${albumTemplateId}/page-templates`, pageTemplate);
    return response.data;
  },

  /** Update an existing page template within a collection (admin only). */
  async updatePageTemplate(albumTemplateId, ptId, pageTemplate) {
    const response = await apiClient.put(`/templates/${albumTemplateId}/page-templates/${ptId}`, pageTemplate);
    return response.data;
  },

  /** Rename / update description of an album template collection (admin only). */
  async updateAlbumTemplate(albumTemplateId, data) {
    const response = await apiClient.patch(`/templates/${albumTemplateId}`, data);
    return response.data;
  },

  /** Remove a page template from an album template collection (admin only). */
  async removePageTemplate(albumTemplateId, ptId) {
    const response = await apiClient.delete(`/templates/${albumTemplateId}/page-templates/${ptId}`);
    return response.data;
  },

  /**
   * Get the session-level AI cost summary (aggregated across all steps).
   * @param {string} sessionId
   * @param {boolean} [refresh=false]  When true the server will recompute before returning.
   * @returns {Promise<Object>} { success, sessionId, aiCostSummary }
   */
  async getSessionAiCostSummary(sessionId, refresh = false) {
    const url = `/session/${sessionId}/ai-cost-summary${refresh ? '?refresh=1' : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },
};

export default albumService;
